import type { JSX, ReactNode } from 'react'

import {
  ActivityIcon,
  ChartColumnIcon,
  Columns3Icon,
  HoverCard,
  Notice,
  Text,
  ToggleGroup,
  TrendingUpIcon,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { ResponsiveSegmentedControl } from '../../components/ResponsiveSegmentedControl'
import { formatServiceList } from '../../utils'

import { ChartTypePreview } from './ChartTypePreview'
import styles from './MetricsSection.module.css'

// Hover-popover preview graphics, hosted in the shared buffer-publish bucket.
const CHART_PREVIEW_BASE =
  'https://buffer-publish.s3.us-east-1.amazonaws.com/images/insights'
const barPreview = `${CHART_PREVIEW_BASE}/bar-chart-horizontal.svg`
const linePreview = `${CHART_PREVIEW_BASE}/line-chart.svg`
const growthPreview = `${CHART_PREVIEW_BASE}/growth-chart.svg`
const stackedBarPreview = `${CHART_PREVIEW_BASE}/stacked-bar.svg`

export type ChartViewOption<T extends string> = {
  value: T
  label: string
  /** Preview graphic shown at the top of the hover popover. */
  preview: string
  /** Bold title in the popover (e.g. "Line chart"). */
  infoTitle: string
  /** Sentence under the title explaining what the view is good for. */
  description: string
}

export type ChartViewDropdownOption<T extends string> = {
  value: T
  label: string
  icon: JSX.Element
}

// Followers can be viewed as a per-channel distribution (default), a net-change
// breakdown, or the original over-time line.
export enum FollowersView {
  BY_CHANNEL = 'byChannel',
  GROWTH = 'growth',
  OVER_TIME = 'overTime',
}

export const isFollowersView = (value: string): value is FollowersView =>
  Object.values(FollowersView).some((view) => view === value)

// Each view's toggle button carries a hover popover: a small preview graphic
// over a bold title and a one-line explanation (copy from the Figma spec).
export const FOLLOWERS_VIEW_OPTIONS: Array<ChartViewOption<FollowersView>> = [
  {
    value: FollowersView.BY_CHANNEL,
    label: 'Bar',
    preview: barPreview,
    infoTitle: 'Bar chart',
    description:
      'Compares current follower counts across your channels, ranked highest first.',
  },
  {
    value: FollowersView.OVER_TIME,
    label: 'Line',
    preview: linePreview,
    infoTitle: 'Line chart',
    description:
      'Tracks changes over time. Good for spotting when things pick up or slow down.',
  },
  {
    value: FollowersView.GROWTH,
    label: 'Growth',
    preview: growthPreview,
    infoTitle: 'Growth chart',
    description:
      'Shows net follower change per period. Bars above zero are gains; below are losses.',
  },
]

// Options for the collapsed (dropdown) form of the chart-type toggle.
export const FOLLOWERS_VIEW_DROPDOWN_OPTIONS: Array<
  ChartViewDropdownOption<FollowersView>
> = [
  {
    value: FollowersView.BY_CHANNEL,
    label: 'Bar',
    icon: <ChartColumnIcon size="small" />,
  },
  {
    value: FollowersView.OVER_TIME,
    label: 'Line',
    icon: <ActivityIcon size="small" />,
  },
  {
    value: FollowersView.GROWTH,
    label: 'Growth',
    icon: <TrendingUpIcon size="small" />,
  },
]

// Posts can be viewed as a ranked horizontal bar (default) — total posts per
// channel — or channel segments stacked into one bar per day over time.
export type PostsView = 'bar' | 'stackedBar'

export const isPostsView = (value: string): value is PostsView =>
  value === 'bar' || value === 'stackedBar'

export const POSTS_VIEW_OPTIONS: Array<ChartViewOption<PostsView>> = [
  {
    value: 'bar',
    label: 'Bar',
    preview: barPreview,
    infoTitle: 'Bar chart',
    description:
      'Shows volume per period. Good for comparing activity over time.',
  },
  {
    value: 'stackedBar',
    label: 'Stacked Bar',
    preview: stackedBarPreview,
    infoTitle: 'Stacked bar chart',
    description:
      'Breaks post volume by channel. Good for seeing your content mix over time.',
  },
]

// Options for the collapsed (dropdown) form of the chart-type toggle.
export const POSTS_VIEW_DROPDOWN_OPTIONS: Array<
  ChartViewDropdownOption<PostsView>
> = [
  {
    value: 'bar',
    label: 'Bar',
    icon: <ChartColumnIcon size="small" />,
  },
  {
    value: 'stackedBar',
    label: 'Stacked Bar',
    icon: <Columns3Icon size="small" />,
  },
]

export function ChartViewToggle<T extends string>({
  value,
  onChange,
  isValue,
  options,
  dropdownOptions,
  'aria-label': ariaLabel,
}: Readonly<{
  value: T
  onChange: (value: T) => void
  isValue: (value: string) => value is T
  options: ReadonlyArray<ChartViewOption<T>>
  dropdownOptions: ReadonlyArray<ChartViewDropdownOption<T>>
  'aria-label'?: string
}>): JSX.Element {
  return (
    <ResponsiveSegmentedControl
      value={value}
      onChange={(next) => {
        if (isValue(next)) onChange(next)
      }}
      options={dropdownOptions}
    >
      <ToggleGroup
        value={value}
        onChange={(next) => {
          if (isValue(next)) onChange(next)
        }}
        aria-label={ariaLabel}
      >
        {options.map((option) => {
          const descriptionId = `chart-type-info-${option.value}`
          return (
            // HoverCard (hover, white surface) explains each view; clicking the
            // item already selects it, so a hover affordance fits better than a
            // click popover.
            <HoverCard key={option.value} closeDelay="fast">
              <HoverCard.Trigger>
                {/* span isolates the trigger's data-state from the toggle item's
                    data-state, so the selected styling is not overridden */}
                <span className={styles.toggleTooltipWrapper}>
                  <ToggleGroup.Item
                    value={option.value}
                    aria-describedby={descriptionId}
                  >
                    {option.label}
                  </ToggleGroup.Item>
                  {/* Sighted users get the copy from the HoverCard; this exposes
                      the same text to screen readers. */}
                  <VisuallyHidden id={descriptionId}>
                    {`${option.infoTitle}. ${option.description}`}
                  </VisuallyHidden>
                </span>
              </HoverCard.Trigger>
              <HoverCard.Content
                side="bottom"
                align="start"
                arrow
                className={styles.chartTypeInfoCard}
              >
                <ChartTypePreview src={option.preview} />
                <div className={styles.chartTypeInfoText}>
                  <Text size="sm" weight="semibold">
                    {option.infoTitle}
                  </Text>
                  <Text size="sm" color="subtle">
                    {option.description}
                  </Text>
                </div>
              </HoverCard.Content>
            </HoverCard>
          )
        })}
      </ToggleGroup>
    </ResponsiveSegmentedControl>
  )
}

export function ExcludedServicesNotice({
  serviceNames,
  children,
}: Readonly<{
  serviceNames: string[]
  children: ReactNode
}>): JSX.Element | null {
  if (serviceNames.length === 0) return null
  return (
    <Notice variant="neutral" className={styles.excludedNotice}>
      <Notice.Text>
        <Text as="span" weight="bold">
          {formatServiceList(serviceNames)}
        </Text>{' '}
        {children}
      </Notice.Text>
    </Notice>
  )
}
